/**
 * Quando abrir o tour do PharuERP (ADR-0104). Montado uma vez no shell.
 *
 * - Primeiro acesso de QUALQUER pessoa: abre sozinho na Visão Geral, quando o
 *   `/api/me` mostra que ela ainda não viu a versão atual do tour. O registro
 *   fica no servidor, por pessoa (`preferencias.tours.erp`), e não no
 *   navegador: trocar de aparelho não reabre, e o logout não apaga.
 * - "Rever o tour" (menu do usuário ou botão de ajuda): leva à Visão Geral e
 *   abre de novo.
 *
 * Os passos ficam num pedaço separado do bundle, baixado só quando abre.
 */
import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { gravarTour } from "../../services/onboarding";

import { aoPedirTour } from "./controle";

const TourGuiadoDoErp = lazy(() => import("./TourGuiadoDoErp"));

/** Suba quando o tour mudar o bastante para todo mundo precisar vê-lo de novo. */
export const VERSAO_DO_TOUR_ERP = 1;
const ROTA_DO_TOUR = "/visao-geral";
// A Visão Geral monta os quadros e a lista antes de o tour apontar para eles.
const ESPERA_ANTES_DE_ABRIR_MS = 900;

export function precisaDoTour(usuario) {
  const preferencias = usuario?.preferencias;
  // Sem a chave ainda não sabemos o que o servidor tem (a resposta do login
  // não traz preferências): não abre até o /api/me chegar.
  if (!preferencias || typeof preferencias !== "object") return false;
  const visto = preferencias.tours?.erp;
  return !visto || Number(visto.versao) < VERSAO_DO_TOUR_ERP;
}

export default function TourDoErp() {
  const { usuario, authenticated, request, primeiroAcesso } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [aberto, setAberto] = useState(false);
  const [vistoNestaSessao, setVistoNestaSessao] = useState(false);
  const pedidoPendente = useRef(false);
  const naRotaDoTour = location.pathname === ROTA_DO_TOUR;

  useEffect(() => {
    // `primeiroAcesso` = troca de senha obrigatória: o tour espera a pessoa entrar de fato.
    // O aviso de 2FA é uma faixa, não bloqueia — o tour abre por cima dela.
    if (!authenticated || aberto || vistoNestaSessao || primeiroAcesso) return undefined;
    if (!naRotaDoTour || !precisaDoTour(usuario)) return undefined;
    const t = setTimeout(() => setAberto(true), ESPERA_ANTES_DE_ABRIR_MS);
    return () => clearTimeout(t);
  }, [authenticated, aberto, vistoNestaSessao, primeiroAcesso, naRotaDoTour, usuario]);

  useEffect(
    () =>
      aoPedirTour(() => {
        if (naRotaDoTour) {
          setAberto(true);
          return;
        }
        pedidoPendente.current = true;
        navigate(ROTA_DO_TOUR);
      }),
    [naRotaDoTour, navigate],
  );

  useEffect(() => {
    if (!naRotaDoTour || !pedidoPendente.current) return undefined;
    pedidoPendente.current = false;
    const t = setTimeout(() => setAberto(true), ESPERA_ANTES_DE_ABRIR_MS);
    return () => clearTimeout(t);
  }, [naRotaDoTour]);

  const fechar = useCallback(
    (desfecho) => {
      setAberto(false);
      setVistoNestaSessao(true);
      // Falhar aqui não merece aviso vermelho: no pior caso o tour reaparece no próximo login.
      gravarTour(request, { desfecho, versao: VERSAO_DO_TOUR_ERP }).catch(() => {});
    },
    [request],
  );

  if (!aberto) return null;
  return (
    <Suspense fallback={null}>
      <TourGuiadoDoErp usuario={usuario} onFechar={fechar} />
    </Suspense>
  );
}
